import { parseEventData } from '@utils/eventsHelpers'
import { firestore, storage, auth } from '@utils/firebase'
import { getUsersName } from '@utils/strings'
import {
  Event,
  CreateEventData,
  EventInterestStatus,
  EventAttendee,
} from '@Interfaces/Data/EventInterface'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { User } from '@Interfaces/Data/UserInterface'
import { ApiResponse } from '@Interfaces/Api/ResponsesInterface'
import { ApiError } from '@Interfaces/Api/ErrorsInterface'
import {
  UnsubscribeFunction,
  ListenerCallback,
  ErrorCallback,
} from '@Interfaces/Services/ServiceTypes'

/**
 * Create a new event
 */
export const createAnEvent = async (data: CreateEventData): Promise<ApiResponse<string>> => {
  try {
    const userId = auth.currentUser?.uid
    if (!userId) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const eventDetail = {
      coverPhoto: '',
      waiver: '',
      active: true,
      about: data?.about,
      createdAt: new Date(),
      updatedAt: new Date(),
      creator: userId,
      categories: data?.categories,
      date: data?.date,
      endTime: data?.endTime,
      lowercaseName: data?.name?.toLowerCase(),
      meetingURL: data?.meetingURL || '',
      mode: data?.mode,
      name: data?.name,
      orgId: data?.orgId,
      volunteersNeeded: data?.noOfVolunteers === 'Limited' ? data?.volunteersNeeded : null,
      orgName: data?.orgName,
      orgType: data?.orgType,
      passCode: data?.passCode || '',
      repeat: data?.repeat || false,
      revenue: 'unpaid',
      startTime: data?.startTime,
      trendingScore: 0,
      type: 'event',
      latLng: data.latLng || null,
      address: data.address || null,
      shifts: data?.shifts || null,
      distanceInM: 0,
    }

    const eventRef = firestore.collection('events')

    let eventId: string | undefined = data?.id
    if (data?.id) {
      await eventRef.doc(data.id).update(eventDetail)
    } else {
      const newEventDoc = await eventRef.add(eventDetail)
      eventId = newEventDoc.id
    }

    if (!eventId) {
      return {
        success: false,
        error: 'Failed to create event',
        data: undefined,
      }
    }

    let coverUrl = ''
    if (data.coverPhoto?.dataUrl) {
      const coverRef = storage.ref(`eventCoverPhotos/${eventId}/cover-photo`)
      await coverRef.putString(data.coverPhoto.dataUrl, 'data_url')
      coverUrl = await coverRef.getDownloadURL()
    }

    let waiverUrl = ''
    if (data.uploadWaiver === 'Yes' && data.waiver?.url) {
      const waiverRef = storage.ref(`eventWaivers/${eventId}/waiver`)
      await waiverRef.putString(data.waiver.url, 'data_url')
      waiverUrl = await waiverRef.getDownloadURL()
    }

    await eventRef.doc(eventId).update({
      coverPhoto: coverUrl || '',
      waiver: waiverUrl || '',
    })

    return {
      success: true,
      data: eventId,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create event',
      data: undefined,
    }
  }
}

/**
 * Alias for createAnEvent (for backward compatibility)
 */
export const createEvent = createAnEvent

/**
 * Get file metadata from storage URL
 */
export const getFileMetadata = async (url: string): Promise<ApiResponse<any>> => {
  try {
    const storageRef = storage.refFromURL(url)
    const metadata = await storageRef.getMetadata()

    return {
      success: true,
      data: metadata,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get file metadata',
      data: undefined,
    }
  }
}

/**
 * Listen for events for an organization
 * Returns unsubscribe function
 */
export const getEvents = (
  organization: Org,
  onUpdate: (upcoming: Event[], past: Event[]) => void,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return firestore
    .collection('events')
    .where('orgId', '==', organization.id)
    .orderBy('date', 'desc')
    .onSnapshot(
      (querySnapshot) => {
        const upcomingEvents: Event[] = []
        const pastEvents: Event[] = []

        if (querySnapshot.empty) {
          onUpdate([], [])
          return
        }

        querySnapshot.forEach((doc) => {
          const event = parseEventData(doc)
          if (!event) return

          const nowSec = Date.now() / 1000
          let startSec = 0
          if (event.startTime instanceof Date) {
            startSec = event.startTime.getTime() / 1000
          } else if (typeof event.startTime === 'number') {
            startSec = event.startTime
          }
          if (startSec > 0 && startSec < nowSec) {
            pastEvents.push(event as unknown as Event)
          } else {
            upcomingEvents.push(event as unknown as Event)
          }
        })

        onUpdate(upcomingEvents, pastEvents)
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message,
          details: error,
        })
      }
    )
}

/**
 * Update event interest status (interested/going)
 */
export const updateEventInterestStatus = async (
  eventId: string,
  interested: boolean | null,
  going: boolean | null
): Promise<ApiResponse<EventInterestStatus>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const data: any = {
      eventId,
      lastUpdated: new Date(),
      userId: uid,
    }

    if (interested !== null) data.interested = !interested
    if (going !== null) data.going = !going

    if (data.going) data.interested = false

    await firestore.collection('event_interest').doc(`${eventId}-${uid}`).set(data, { merge: true })

    return {
      success: true,
      data: {
        interested: interested !== null ? !interested : null,
        going: going !== null ? !going : null,
      },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update event interest status',
      data: undefined,
    }
  }
}

/**
 * Delete an event
 */
export const deleteEvent = async (event: Event): Promise<ApiResponse<boolean>> => {
  if (!event?.id) {
    return {
      success: false,
      error: 'Event ID is required',
      data: undefined,
    }
  }
  try {
    await firestore.collection('events').doc(event.id).delete()

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete event',
      data: undefined,
    }
  }
}

/**
 * Check in an event attendee
 */
export const checkInEventAttendee = async (
  eventId: string,
  userId: string
): Promise<ApiResponse<boolean>> => {
  try {
    const eventInterestRef = firestore.collection('event_interest').doc(`${eventId}-${userId}`)
    const doc = await eventInterestRef.get()

    if (!doc.exists) {
      return {
        success: false,
        error: 'User not listed as attending. Please have them RSVP and try again',
        data: undefined,
      }
    }

    await eventInterestRef.update({ attended: true })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check in attendee',
      data: undefined,
    }
  }
}

/**
 * Undo check in for an event attendee
 */
export const undoCheckInEventAttendee = async (
  eventId: string,
  userId: string
): Promise<ApiResponse<boolean>> => {
  try {
    const eventInterestRef = firestore.collection('event_interest').doc(`${eventId}-${userId}`)
    const doc = await eventInterestRef.get()

    if (doc.exists) {
      await eventInterestRef.update({ attended: false })
    }

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to undo check in',
      data: undefined,
    }
  }
}

/**
 * Listen for event attendees
 * Returns unsubscribe function
 * Note: This requires getUsersFromMembersPromise from UserService
 */
export const getEventAttendees = (
  eventId: string,
  onUpdate: ListenerCallback<EventAttendee[]>,
  onError?: ErrorCallback,
  getUsersFromMembersPromise?: (userId: string, obj: any) => Promise<any>
): UnsubscribeFunction => {
  return firestore
    .collection('event_interest')
    .where('eventId', '==', eventId)
    .onSnapshot(
      (querySnapshot) => {
        const temp: any[] = []
        querySnapshot.forEach((doc) => {
          const attendee = {
            id: doc.id,
            ...doc.data(),
          }
          temp.push(attendee)
        })

        if (getUsersFromMembersPromise) {
          const promises = temp.map((x) => getUsersFromMembersPromise(x.userId, x))
          Promise.all(promises)
            .then((users) => {
              const sorted = users.sort((a, b) =>
                getUsersName(a).toLowerCase() > getUsersName(b).toLowerCase() ? 1 : -1
              )
              onUpdate(sorted as EventAttendee[])
            })
            .catch((error) => {
              onError?.({
                code: 'firestore/error',
                message: error.message,
                details: error,
              })
            })
        } else {
          onUpdate(temp as EventAttendee[])
        }
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message,
          details: error,
        })
      }
    )
}

/**
 * Check if custom event URL exists
 */
export const checkCustomEventUrls = async (customUrl: string): Promise<ApiResponse<boolean>> => {
  try {
    const eventQuery = await firestore
      .collection('events')
      .where('customUrl', '==', customUrl)
      .get()

    return {
      success: true,
      data: eventQuery.size > 0,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check custom URL',
      data: undefined,
    }
  }
}

/**
 * Save custom event URL
 */
export const saveCustomEventUrl = async (
  eventId: string,
  customUrl: string
): Promise<ApiResponse<boolean>> => {
  try {
    await firestore.collection('events').doc(eventId).update({ customUrl })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to save custom URL',
      data: undefined,
    }
  }
}

/**
 * Listen for donations to an event
 * Returns unsubscribe function
 */
export const getEventDonations = (
  selectedEvent: Event,
  onUpdate: ListenerCallback<any[]>,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return firestore
    .collection('donations')
    .where('eventId', '==', selectedEvent?.id)
    .orderBy('date', 'desc')
    .onSnapshot(
      (querySnapshot) => {
        const history: any[] = []
        querySnapshot.forEach((doc) => {
          history.push({
            ...doc.data(),
            id: doc.id,
          })
        })
        onUpdate(history)
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message,
          details: error,
        })
      }
    )
}
